// Money is graded in three steps, mirroring ORDER_MONEY_ROLES /
// CUSTOMER_MONEY_ROLES / AGGREGATE_MONEY_ROLES in backend/models/core.py. These
// are for layout only — the API omits the fields for everyone else, so a page
// that forgets to check shows nothing rather than showing a number.
export const FINANCIAL_ROLES = ['admin', 'receptionist', 'accountant', 'city_office_manager'];
export const CUSTOMER_MONEY_ROLES = ['admin', 'receptionist', 'accountant'];
export const AGGREGATE_MONEY_ROLES = ['admin', 'accountant'];

function hasAnyRole(user, allowed) {
  const roles = user?.roles?.length ? user.roles : [user?.role];
  return roles.some((r) => allowed.includes(r));
}

export function canSeeFinancials(user) {
  return hasAnyRole(user, FINANCIAL_ROLES);
}

export function canSeeCustomerMoney(user) {
  return hasAnyRole(user, CUSTOMER_MONEY_ROLES);
}

// what the firm billed, collected and is owed — management and accounts only
export function canSeeAggregateMoney(user) {
  return hasAnyRole(user, AGGREGATE_MONEY_ROLES);
}

// Whoever may book an order may go back and correct one. Mirrors
// ORDER_CREATOR_ROLES in backend/models/core.py, which is what PUT /api/orders
// checks before it will touch the items or the buyer.
export const ORDER_EDITOR_ROLES = [
  'admin', 'receptionist', 'designer', 'accountant',  // 'head_designer' — post open
  'supervisor', 'city_office_manager',
];

export function canEditOrders(user) {
  const roles = user?.roles?.length ? user.roles : [user?.role];
  return roles.some((r) => ORDER_EDITOR_ROLES.includes(r));
}

// Declaring a job finished without walking it through the floor. Mirrors
// ORDER_CLOSEOUT_ROLES in backend/models/core.py.
export const ORDER_CLOSEOUT_ROLES = ['admin', 'supervisor', 'dispatcher', 'receptionist'];

export function canCompleteOrders(user) {
  return hasAnyRole(user, ORDER_CLOSEOUT_ROLES);
}

// The job card PDF carries the customer, the prices and every spec, so it stops
// at the same roles — mirrors ORDER_CREATOR_ROLES on GET /api/jobcard/{id}/pdf.
export function canPrintJobCard(user) {
  return hasAnyRole(user, ORDER_EDITOR_ROLES);
}

// A password an admin handed out is temporary: the API answers
// `password_change_required` to every other call until it is changed. Machine
// accounts are exempt here as they are server side — shared floor logins with
// nobody to rotate them.
export function mustChangePassword(user) {
  if (!user?.must_change_password) return false;
  return !hasAnyRole(user, ['machine', 'machine_man']);
}

// Who hands design work out. With the head designer post open every designer
// claims their own from the queue, so the picker — and the designer list it
// reads — belong to the desk roles that still route work. Mirrors the roles
// on PUT /api/design/{id}/assign.
export const DESIGN_ASSIGNER_ROLES = ['admin', 'receptionist'];

export function canAssignDesigners(user) {
  const roles = user?.roles?.length ? user.roles : [user?.role];
  return roles.some((r) => DESIGN_ASSIGNER_ROLES.includes(r));
}

// Who may open the Users module. The API's `user_manager` also admits
// supervisors, who have never had the page in the sidebar and do not get it
// here either — this is the web module's own list.
export const USER_MANAGER_ROLES = ['admin', 'receptionist', 'designer'];

// Accounts only an admin may create, edit, deactivate or reset — mirrors
// PRIVILEGED_ROLES in backend/models/core.py, which is what the API enforces.
// Held here so the page does not offer a manager a button that will 403.
export const PRIVILEGED_ROLES = [
  'admin', 'receptionist', 'supervisor', 'accountant', 'city_office_manager',
];

export function canManageAccount(actor, account) {
  const actorRoles = actor?.roles?.length ? actor.roles : [actor?.role];
  if (actorRoles.includes('admin')) return true;
  const accountRoles = account?.roles?.length ? account.roles : [account?.role];
  return !accountRoles.some((r) => PRIVILEGED_ROLES.includes(r));
}

// Product type code → label mappings
// These are dynamically populated from /api/admin/product-configs/types-list
// Starts empty — populated on first fetchProductTypes() call.
// Pages should use getProductTypeName() which returns the code itself as fallback.

export const DEPT_NAMES = {};
export const DEPT_FULL_NAMES = {};

// Dynamic product type fetcher — call once and cache
let _cachedTypes = null;
let _fetchPromise = null;

export async function fetchProductTypes(api) {
  if (_cachedTypes) return _cachedTypes;
  if (_fetchPromise) return _fetchPromise;

  _fetchPromise = api.get('/admin/product-configs/types-list')
    .then(res => {
      _cachedTypes = res.data;
      // Populate the shared maps with fresh data from DB
      if (_cachedTypes.types_full) {
        Object.keys(DEPT_FULL_NAMES).forEach(k => delete DEPT_FULL_NAMES[k]);
        Object.assign(DEPT_FULL_NAMES, _cachedTypes.types_full);
      }
      if (_cachedTypes.types_short) {
        Object.keys(DEPT_NAMES).forEach(k => delete DEPT_NAMES[k]);
        Object.assign(DEPT_NAMES, _cachedTypes.types_short);
      }
      return _cachedTypes;
    })
    .catch(() => {
      // On error return whatever we have (possibly empty)
      return { types_map: DEPT_FULL_NAMES, types_short: DEPT_NAMES, types_full: DEPT_FULL_NAMES };
    })
    .finally(() => { _fetchPromise = null; });

  return _fetchPromise;
}

// Helper to get product type name (uses cached or fallback to code)
// An item booked without a product type carries this code until its job card
// says what it is. It is a state, not a department, so it reads as one.
export const UNCLASSIFIED = 'UNCLASSIFIED';

export function getProductTypeName(code, full = false) {
  if (!code) return '';
  if (code === UNCLASSIFIED) return 'Type not set';
  return full ? (DEPT_FULL_NAMES[code] || code) : (DEPT_NAMES[code] || code);
}

// Initialize product types as soon as a token is available
// Called from AuthContext after successful login
export function initProductTypes(api) {
  const token = localStorage.getItem('leroy_token');
  if (token) {
    fetchProductTypes(api).catch(() => {});
  }
}
